import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth, AppRole } from './AuthContext';

export type PermAction = 'view' | 'create' | 'update' | 'delete';
export type PermModule =
  | 'dashboard' | 'clients' | 'prestations' | 'artistes' | 'propositions'
  | 'devis' | 'contrats' | 'facturation' | 'relances' | 'agenda'
  | 'suivi' | 'journal' | 'doublons' | 'corbeille' | 'export_comptable'
  | 'parametres' | 'utilisateurs';

export interface PermissionRow {
  role: AppRole;
  module: string;
  action: PermAction;
  allowed: boolean;
}

interface PermissionsCtx {
  matrix: PermissionRow[];
  loading: boolean;
  can: (module: PermModule | string, action: PermAction) => boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<PermissionsCtx | null>(null);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { roles, hasRole, user } = useAuth();
  const [matrix, setMatrix] = useState<PermissionRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setMatrix([]); setLoading(false); return; }
    const { data } = await supabase.from('role_permissions').select('role, module, action, allowed');
    setMatrix((data || []) as PermissionRow[]);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const can = useCallback((module: string, action: PermAction) => {
    if (hasRole('admin')) return true;
    if (!roles.length) return false;
    return matrix.some(p => roles.includes(p.role) && p.module === module && p.action === action && p.allowed);
  }, [matrix, roles, hasRole]);

  return <Ctx.Provider value={{ matrix, loading, can, refresh: load }}>{children}</Ctx.Provider>;
}

export function usePermissions() {
  const c = useContext(Ctx);
  if (!c) throw new Error('usePermissions must be used within PermissionsProvider');
  return c;
}
